import type { Recipient, RecipientFilters } from '../components/recipient_selection/types'

export type { Recipient, RecipientFilters }

/** A file attached to an email, as listed for display. */
export interface EmailAttachmentInfo {
  filename: string
  byteSize: number
  contentType?: string
}

/**
 * An attachment the form can submit. The signed id is what re-attaches the
 * file, so the server only sends it to the form, not to listings.
 */
export interface EmailAttachment extends EmailAttachmentInfo {
  /** ActiveStorage signed id — what the form submits to attach the file. */
  signedId: string
}

/** What SendGrid will accept, handed down by the server so both ends agree. */
export interface AttachmentLimits {
  maxFileBytes: number
  maxTotalBytes: number
}

export const DEFAULT_ATTACHMENT_LIMITS: AttachmentLimits = {
  maxFileBytes: 22_500_000,
  maxTotalBytes: 30_000_000,
}

/** Base64 encodes three bytes as four, which is what the size limits apply to. */
export const encodedSize = (bytes: number): number => Math.ceil(bytes / 3) * 4

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1_000) return `${bytes} B`
  if (bytes < 1_000_000) return `${(bytes / 1_000).toFixed(1)} KB`
  return `${(bytes / 1_000_000).toFixed(1)} MB`
}

export interface ManualEmail {
  id: string
  campaignName: string
  subjectLine: string
  senderEmail: string
  senderName: string
  replyToEmail?: string
  htmlContent?: string
  designJson?: Record<string, unknown>
  recipientCount: number
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed'
  addToCalendar: boolean
  attachments?: EmailAttachmentInfo[]
  createdAt: string
  sentAt?: string
  updatedAt?: string
}

/** Template types supported by `AutomatedEmailTemplate` on the backend. */
export const AUTOMATED_EMAIL_TEMPLATE_TYPES = [
  'registration_acknowledgement',
  'registration_confirmation',
  'reminder_1_day',
  'reminder_1_week',
  'thank_you',
  'e_certificate',
] as const

export type AutomatedEmailTemplateType = (typeof AUTOMATED_EMAIL_TEMPLATE_TYPES)[number]

/**
 * The one template type edited somewhere other than the shared automated email
 * page: its email body is only half the record, the certificate design is the
 * other half, so both live on the e-certificate builder's two-tab page.
 *
 * Mirrors `AutomatedEmailTemplate::E_CERTIFICATE_TEMPLATE_TYPE`, which
 * `EmailBlastsController#edit_automated` compares against to redirect there.
 */
export const E_CERTIFICATE_TEMPLATE_TYPE: AutomatedEmailTemplateType = 'e_certificate'

/**
 * Who an automated email goes out to. Only meaningful for the types in
 * `AUDIENCE_SELECTABLE_TEMPLATE_TYPES`.
 */
export type AutomatedEmailAudience = 'all' | 'attended'

/**
 * The template types whose audience staff get to choose. Every other type is
 * transactional, so it always goes to everyone.
 *
 * Mirrors `AutomatedEmailTemplate::AUDIENCE_SELECTABLE_TEMPLATE_TYPES` in
 * app/models/automated_email_template.rb, which is the authority — it is the
 * validation that refuses a non-default audience on any other type. Declared
 * once here so the form control and the list badge cannot drift apart.
 */
export const AUDIENCE_SELECTABLE_TEMPLATE_TYPES: readonly AutomatedEmailTemplateType[] = [
  'thank_you',
  'e_certificate',
]

/** Whether this template offers a choice of audience. */
export const isAudienceSelectable = (
  templateType: AutomatedEmailTemplateType | undefined
): boolean => !!templateType && AUDIENCE_SELECTABLE_TEMPLATE_TYPES.includes(templateType)

/**
 * The template types with no schedule at all: nothing triggers them, staff
 * release them by hand.
 *
 * Mirrors `AutomatedEmailTemplate::MANUAL_SEND_TEMPLATE_TYPES` in
 * app/models/automated_email_template.rb, which is the authority — the server
 * refuses a manual send for any type outside it.
 */
export const MANUAL_SEND_TEMPLATE_TYPES: readonly AutomatedEmailTemplateType[] = ['e_certificate']

/** Whether this template is sent by hand rather than on a schedule. */
export const isManualSendTemplate = (
  templateType: AutomatedEmailTemplateType | undefined
): boolean => !!templateType && MANUAL_SEND_TEMPLATE_TYPES.includes(templateType)

export const DEFAULT_AUTOMATED_EMAIL_AUDIENCE: AutomatedEmailAudience = 'all'

export const AUTOMATED_EMAIL_AUDIENCE_LABELS: Record<AutomatedEmailAudience, string> = {
  all: 'All registrants',
  attended: 'Attended only',
}

export const AUTOMATED_EMAIL_AUDIENCE_OPTIONS: { label: string; value: AutomatedEmailAudience }[] = [
  { label: AUTOMATED_EMAIL_AUDIENCE_LABELS.all, value: 'all' },
  { label: AUTOMATED_EMAIL_AUDIENCE_LABELS.attended, value: 'attended' },
]

/**
 * Fallback display titles, mirroring the `template_name` defaults seeded by
 * `AutomatedEmailTemplate.defaults`. Used whenever the server has not supplied
 * a `templateName` for a record.
 */
export const AUTOMATED_EMAIL_TEMPLATE_TITLES: Record<AutomatedEmailTemplateType, string> = {
  registration_acknowledgement: 'Registration Acknowledgement',
  registration_confirmation: 'Registration Confirmation',
  reminder_1_day: '1-Day Reminder',
  reminder_1_week: '1-Week Reminder',
  thank_you: 'Thank You',
  e_certificate: 'E-Certificate',
}

export const DEFAULT_AUTOMATED_EMAIL_TITLE = 'Automated Email'

export interface AutomatedEmail {
  id: string
  templateType: AutomatedEmailTemplateType
  templateName: string
  subjectLine: string
  senderName: string
  senderEmail: string
  replyToEmail?: string
  htmlContent?: string
  designJson?: Record<string, unknown>
  enabled: boolean
  audience?: AutomatedEmailAudience
}

/**
 * Resolves the title shown for an automated email: the server-provided
 * `templateName` when present, otherwise the canonical title for its type.
 */
export const getAutomatedEmailTitle = (
  email: Pick<AutomatedEmail, 'templateType' | 'templateName'> | null | undefined
): string => {
  if (!email) return DEFAULT_AUTOMATED_EMAIL_TITLE

  return (
    email.templateName ||
    AUTOMATED_EMAIL_TEMPLATE_TITLES[email.templateType] ||
    DEFAULT_AUTOMATED_EMAIL_TITLE
  )
}

export interface EmailTemplate {
  id: string
  name: string
  description?: string
  thumbnailUrl?: string
  htmlContent: string
  designJson?: Record<string, unknown>
  templateType: string
  systemDefault: boolean
}

export interface EmailFormData {
  id?: string
  campaignName: string
  subjectLine: string
  recipientIds: string[]
  recipientFilters: RecipientFilters
  senderEmail: string
  senderEmailDomain: string
  senderName: string
  replyToEmail: string
  addToCalendar: boolean
  htmlContent: string
  designJson: Record<string, unknown>
  attachments: EmailAttachment[]
}

export interface Event {
  id: number
  name: string
  slug: string
  start_date: string
  end_date: string
}

export const defaultEmailDesignJson = {}

export const defaultEmailHtmlContent = `
<html>

</html>
`
